// The picture panel: the room controls' floating white panel (same .panel styles), for one hang slot.
// Empty slot: the frame choice (panel / large, on walls with a large slot), a drop zone and "Add image".
// Filled slot: the picture as it hangs (cover crop at the frame's aspect), who added it, "Replace image", "Remove".
// Both: the frame colour (oak, white, black, or any colour from the colour field) and the mat (none, five colours,
// or any colour). Empty slot: they go with the upload; filled slot: a change shows here at once and is sent to the
// server (PATCH, the image is not uploaded again) 300 ms after the last change; a colour field previews while it is
// dragged and sends when it is set. Each colour field has an editable hex value beside it (ui/hex-field.ts), sent
// like the colour field. The picture area takes the aspect of the frame the picture hangs in (slots.ts frameFor:
// portrait, landscape or square from the image's own aspect; larger without a mat).
// Files come from the file input or are dropped anywhere on the panel. The markup is built here (not in
// index.html) and the styles are in art-panel.css.
//
// Below: the wall's library (contracts section 8 "Library"): every image uploaded on this wall, newest first, as a
// grid of square thumbnails (buttons). A thumbnail hangs that image in this slot (the server assigns the stored file;
// nothing is uploaded); the one in this frame is marked. Each has a delete button: first press turns it into
// "Delete?" (and says when the image is on the wall: deleting takes it down), the second deletes; it goes back after
// a few seconds, on blur, or when the panel shows something else. Thumbnails are the small files sent at upload; an
// image without one (hung before libraries existed) shows the hung file (already loaded for the wall) and a writer
// sends a thumbnail made from it once.
//
// Read only (docs/plans/admin.md, Phase 2): when the server locks pictures and this browser is not in edit mode
// (permissions.ts canEditArt), the panel shows the hung picture, its title and who added it; the frame choice, the
// buttons, the library and the note are hidden.

import './art-panel.css';
import { ART_MAT_DEFAULT, ART_MAT_NONE, type ArtEntry, type ArtFinish, type ArtLibrary, type LibraryItem } from '@lr3d/shared';
import { bindHexField, hexInput } from '../ui/hex-field';
import { frameless, OVERLAY_KIND, type Artworks } from './artworks';
import { canEditArt } from './permissions';
import { frameFor, type SlotSet } from './slots';
import { isTile, tileLayout } from './tile';
import { ACCEPT, ArtError, deleteFromLibrary, hangFromLibrary, makeThumb, prepareImage, removeArt, setArtFrame, uploadArt, uploadThumb } from './upload';

/** Frame colours offered in the panel (dot colour = the finish albedo, frame.ts FINISH_ALBEDO, in sRGB). */
const FINISHES: { value: Exclude<ArtFinish, 'custom'>; label: string; hex: string }[] = [
  { value: 'oak', label: 'Oak', hex: '#957652' },
  { value: 'warm_white', label: 'White', hex: '#faf9f6' },
  { value: 'black', label: 'Black', hex: '#343332' },
];
/**
 * Mats offered in the panel: none, the default grey-beige (ART_MAT_DEFAULT, frame.ts MAT_ALBEDO), and neutrals for
 * the oak, white and black frames. Any other colour comes from the colour field.
 */
const MATS: { value: string; label: string }[] = [
  { value: ART_MAT_NONE, label: 'No mat' },
  { value: ART_MAT_DEFAULT, label: 'Grey-beige' },
  { value: '#f3efe4', label: 'Warm white' },
  { value: '#b9b2a5', label: 'Stone' },
  { value: '#4a4845', label: 'Charcoal' },
  { value: '#1f1e1d', label: 'Black' },
];
/** A frame choice: a preset finish, or 'custom' with its colour; and the mat (missing = the default). */
export interface FrameChoice { finish: ArtFinish; color?: string; mat?: string }
const sameChoice = (a: FrameChoice, b: FrameChoice) =>
  a.finish === b.finish && (a.finish !== 'custom' || a.color === b.color) && (a.mat ?? ART_MAT_DEFAULT) === (b.mat ?? ART_MAT_DEFAULT);
/** A row of colour dots with a colour field and its hex value (frame colour, mat). */
interface ColourRow { dots: HTMLButtonElement[]; color: HTMLInputElement; hex: HTMLInputElement; hexView: { show(hex: string): void }; name: HTMLElement }
/** A filled slot's finish is sent this long after the last change (arrow keys move through the colours). */
const FINISH_SEND_MS = 300;
const PICTURE_NOTE = 'Pictures are shared with everyone in the room. JPEG, PNG, WebP or GIF; animated GIF or WebP up to 8 MB, 800 px and 300 frames. Small square images repeat as a pattern.';
/** The Basement overlay's slot (overlay/overlay.ts shows the first frame of an animation, and no tiles). */
const OVERLAY_NOTE = 'The overlay image is shared with everyone in the room. It shows over the view as you walk toward its place (the centre circle in the Basement, the floor opening in the hallway). JPEG, PNG, WebP or GIF; an animated image shows its first frame.';
const OFFLINE_TEXT = 'Not connected to the room server: pictures cannot be changed now.';
/** The "Delete?" state of a library thumbnail goes back after this long. */
const CONFIRM_MS = 5000;

export interface ArtPanelContext {
  slots: SlotSet;
  artworks: Artworks;
  /** Server origin, room and this connection's token; null while not connected. */
  writer(): { base: string; room: string; token: string } | null;
  fileUrl(e: ArtEntry): string;
}

const h = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, text?: string): HTMLElementTagNameMap[K] => {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  if (text !== undefined) el.textContent = text;
  return el;
};

export class ArtPanel {
  readonly el: HTMLElement;
  isOpen = false;
  slot: string | null = null;
  /** The panel closed (focus goes back to the view). */
  onClose: () => void = () => {};
  /** The frame choice moved to another slot (panel <-> large). */
  onSelectSlot: (id: string) => void = () => {};
  /** A picture was added or removed here (applied locally before the broadcast arrives). */
  onApplied: (slot: string, art: ArtEntry | null) => void = () => {};
  /** The room's wall libraries (wall id -> images, newest first). */
  readonly library = new Map<string, LibraryItem[]>();

  private title: HTMLElement;
  private where: HTMLElement;
  private sizeGroup: HTMLFieldSetElement;
  private sizePanel: HTMLInputElement;
  private sizeLarge: HTMLInputElement;
  private sizeNote: HTMLElement;
  private finishRow: ColourRow;
  /** The frame colour and mat rows (hidden for the window view: it has no frame). */
  private finishEl!: HTMLElement;
  private matEl!: HTMLElement;
  private matRow: ColourRow;
  private lib: HTMLElement;
  private libTitle: HTMLElement;
  private libGrid: HTMLUListElement;
  private libEmpty: HTMLElement;
  private libNote: HTMLElement;
  /** The thumbnail whose delete button shows "Delete?" (hash), and its timer. */
  private confirming: { wall: string; hash: string; timer: ReturnType<typeof setTimeout> } | null = null;
  /** Thumbnails this client has made for images without one (once per image). */
  private thumbTried = new Set<string>();
  /** The frame chosen for the next upload (null = the slot's frame default). */
  private uploadChoice: FrameChoice | null = null;
  /**
   * A filled slot's frame change not yet confirmed: the choice, the entry before the change (to undo), the send
   * timer (null once sent or while only previewing), and whether a request is on its way.
   */
  private pending: { slot: string; choice: FrameChoice; before: ArtEntry; timer: ReturnType<typeof setTimeout> | null; inflight: number } | null = null;
  /** Pixel size of the image being uploaded (its frame shape shows in the picture area meanwhile). */
  private localSize: { w: number; h: number } | null = null;
  private drop: HTMLElement;
  private preview: HTMLImageElement;
  private dropText: HTMLElement;
  private meta: HTMLElement;
  private addBtn: HTMLButtonElement;
  private removeBtn: HTMLButtonElement;
  private file: HTMLInputElement;
  private status: HTMLElement;
  private note: HTMLElement;
  private actions: HTMLElement;
  private offline: HTMLElement;
  private busy = false;
  private localUrl: string | null = null;
  private dragDepth = 0;

  constructor(private ctx: ArtPanelContext) {
    const el = (this.el = h('aside', { id: 'art-panel', class: 'panel art-panel', role: 'dialog', 'aria-labelledby': 'art-panel-title', 'aria-hidden': 'true', tabindex: '-1', 'data-native-keys': '' }));
    el.inert = true;
    const head = h('header', { class: 'panel-head' });
    this.title = h('h2', { id: 'art-panel-title' }, 'Picture');
    const close = h('button', { class: 'panel-close', 'aria-label': 'Close panel', type: 'button' });
    close.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 3.5l9 9m0-9l-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    close.addEventListener('click', () => this.close());
    head.append(this.title, close);

    const body = h('div', { class: 'panel-body' });
    this.where = h('p', { class: 'art-where', id: 'art-where' });

    // frame choice (walls with a large slot, empty slots only)
    this.sizeGroup = h('fieldset', { class: 'art-size' });
    this.sizeGroup.append(h('legend', {}, 'Frame'));
    const radio = (value: string, label: string): HTMLInputElement => {
      const id = `art-size-${value}`;
      const wrap = h('label', { class: 'art-size__opt', for: id });
      const input = h('input', { type: 'radio', name: 'art-size', id, value });
      wrap.append(input, h('span', {}, label));
      this.sizeGroup.append(wrap);
      input.addEventListener('change', () => { if (input.checked) this.chooseSize(value as 'panel' | 'large'); });
      return input;
    };
    this.sizePanel = radio('panel', 'In the panel');
    this.sizeLarge = radio('large', 'Large, over the mouldings');
    this.sizeNote = h('p', { class: 'art-size__note', id: 'art-size-note' });
    this.sizeGroup.append(this.sizeNote);

    // frame colour and mat: radiogroups of small dots (the start card's colour dots), one tab stop, arrows move and
    // select; then the room panel's colour field (previews while it moves, sent when it is set) and its hex value
    const row = (key: string, label: string, opts: { value: string; label: string; hex: string }[],
                 pick: (value: string) => void, colour: (hex: string, mode: 'preview' | 'send') => void, start: string): { el: HTMLElement; row: ColourRow } => {
      const wrap = h('div', { class: `art-finish art-finish--${key}` });
      const lab = h('span', { class: 'art-finish__label', id: `art-${key}-label` }, label);
      const group = h('div', { class: 'art-finish__dots', role: 'radiogroup', 'aria-labelledby': `art-${key}-label`, id: `art-${key}` });
      const name = h('span', { class: 'art-finish__name', 'aria-hidden': 'true' });
      const dots: HTMLButtonElement[] = [];
      opts.forEach((o) => {
        const b = h('button', { type: 'button', class: `swatch${o.value === ART_MAT_NONE ? ' swatch--none' : ''}`, role: 'radio', 'aria-label': o.label, title: o.label, [`data-${key}`]: o.value });
        b.style.setProperty('--c', o.hex);
        b.append(h('span', { class: 'swatch-dot', 'aria-hidden': 'true' }));
        b.addEventListener('click', () => pick(o.value));
        dots.push(b);
        group.append(b);
      });
      group.addEventListener('keydown', (e) => {
        const i = dots.indexOf(e.target as HTMLButtonElement);
        if (i < 0) return;
        const n = dots.length;
        const next = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (i + 1) % n
          : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? (i - 1 + n) % n
          : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
        if (next < 0) return;
        e.preventDefault();
        pick(opts[next]!.value);
        dots[next]!.focus();
      });
      const color = h('input', { type: 'color', class: 'art-finish__color', id: `art-${key}-color`, 'aria-label': `${label}: any colour`, title: 'Any colour', value: start });
      color.addEventListener('input', () => colour(color.value.toLowerCase(), 'preview'));
      color.addEventListener('change', () => colour(color.value.toLowerCase(), 'send'));
      // its hex value, editable: a valid value sets the colour field and is sent like it
      const hex = hexInput({ id: `art-${key}-hex`, label: `${label}, hex value`, className: 'art-finish__hex' });
      const hexView = bindHexField(hex, {
        current: () => color.value.toLowerCase(),
        apply: (v) => { color.value = v; colour(v, 'send'); },
      });
      wrap.append(lab, group, color, hex, name);
      return { el: wrap, row: { dots, color, hex, hexView, name } };
    };
    const finish = row('finish', 'Frame colour', FINISHES, (v) => this.choose({ finish: v as ArtFinish }, 'send'),
      (hex, mode) => this.choose({ finish: 'custom', color: hex }, mode), '#8a6a4a');
    this.finishRow = finish.row;
    const mat = row('mat', 'Mat', MATS.map((m) => ({ ...m, hex: m.value === ART_MAT_NONE ? 'transparent' : m.value })),
      (v) => this.choose({ mat: v }, 'send'), (hex, mode) => this.choose({ mat: hex }, mode), '#c8c2b6');
    this.matRow = mat.row;

    // the picture (or the drop zone)
    this.drop = h('div', { class: 'art-drop' });
    // crossorigin on every picture <img>: the same cache entry then serves the wall's texture (a CORS load)
    this.preview = h('img', { class: 'art-drop__img', alt: '', crossorigin: 'anonymous' });
    this.dropText = h('p', { class: 'art-drop__text' }, 'Drop an image here');
    this.drop.append(this.preview, this.dropText);
    this.meta = h('p', { class: 'art-meta' });

    const actions = (this.actions = h('div', { class: 'art-actions' }));
    this.file = h('input', { type: 'file', accept: ACCEPT, class: 'art-file', id: 'art-file', 'aria-label': 'Choose an image file', tabindex: '-1' });
    this.file.addEventListener('change', () => {
      const f = this.file.files?.[0];
      this.file.value = '';
      if (f) void this.upload(f);
    });
    this.addBtn = h('button', { class: 'art-btn art-btn--primary', type: 'button', id: 'art-add' }, 'Add image');
    this.addBtn.addEventListener('click', () => this.file.click());
    this.removeBtn = h('button', { class: 'art-btn', type: 'button', id: 'art-remove' }, 'Remove');
    this.removeBtn.addEventListener('click', () => void this.remove());
    actions.append(this.addBtn, this.removeBtn, this.file);

    this.status = h('p', { class: 'art-status', role: 'status', 'aria-live': 'polite', id: 'art-status' });
    this.offline = h('p', { class: 'art-status art-status--error' }, OFFLINE_TEXT);
    this.offline.hidden = true;
    this.note = h('p', { class: 'panel-note' }, PICTURE_NOTE);

    // the wall's library
    this.lib = h('section', { class: 'art-lib', 'aria-labelledby': 'art-lib-title' });
    this.libTitle = h('h3', { id: 'art-lib-title' }, 'Library');
    this.libGrid = h('ul', { class: 'art-lib__grid', id: 'art-lib-grid', role: 'list' });
    this.libEmpty = h('p', { class: 'art-lib__empty' }, 'Images added on this wall are kept here.');
    this.libNote = h('p', { class: 'art-lib__note', id: 'art-lib-note', 'aria-live': 'polite' });
    this.lib.append(this.libTitle, this.libGrid, this.libEmpty, this.libNote);
    this.libGrid.addEventListener('focusout', (e) => {
      // leaving a "Delete?" button cancels it
      const t = e.target as HTMLElement;
      if (t.classList.contains('art-lib__del') && t.classList.contains('is-confirm') && e.relatedTarget !== t) this.cancelConfirm();
    });

    this.finishEl = finish.el; this.matEl = mat.el;
    body.append(this.where, this.sizeGroup, finish.el, mat.el, this.drop, this.meta, actions, this.status, this.offline, this.lib, this.note);
    el.append(head, body);
    document.body.appendChild(el);

    // clicks inside the panel must not reach the view
    el.addEventListener('pointerdown', (e) => e.stopPropagation());
    // drag and drop anywhere on the panel
    el.addEventListener('dragenter', (e) => { if (this.hasFiles(e)) { e.preventDefault(); this.dragDepth++; el.classList.add('art-dragging'); } });
    el.addEventListener('dragover', (e) => { if (this.hasFiles(e)) { e.preventDefault(); e.dataTransfer!.dropEffect = this.canWrite() ? 'copy' : 'none'; } });
    el.addEventListener('dragleave', () => { if (--this.dragDepth <= 0) { this.dragDepth = 0; el.classList.remove('art-dragging'); } });
    el.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dragDepth = 0;
      el.classList.remove('art-dragging');
      const f = e.dataTransfer?.files?.[0];
      if (f) void this.upload(f);
    });
  }

  open(slot: string): void {
    if (this.slot !== slot) { this.flushFrame(); this.setStatus(''); this.cancelConfirm(false); }
    this.slot = slot;
    this.render();
    if (this.isOpen) return;
    this.isOpen = true;
    this.el.inert = false;
    this.el.removeAttribute('aria-hidden');
    this.el.classList.add('open');
    this.el.focus({ preventScroll: true });
  }

  close(): void {
    if (!this.isOpen) return;
    this.flushFrame();
    this.cancelConfirm(false);
    this.isOpen = false;
    this.el.classList.remove('open');
    this.el.setAttribute('aria-hidden', 'true');
    if (this.el.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    this.el.inert = true;
    this.onClose();
  }

  /** The art state or the connection changed: show the current slot again. */
  refresh(): void {
    if (this.isOpen) this.render();
  }

  /** The room's libraries (welcome or GET /art/<room>): walls missing from it are empty. */
  applyLibrary(all: ArtLibrary): void {
    this.library.clear();
    for (const [wall, items] of Object.entries(all)) this.library.set(wall, items);
    this.refresh();
  }

  /** One wall's new list (broadcast, or a write's answer). */
  setLibrary(wall: string, items: LibraryItem[]): void {
    this.library.set(wall, items);
    if (this.confirming && this.confirming.wall === wall && !items.some((i) => i.hash === this.confirming!.hash)) this.cancelConfirm(false);
    this.refresh();
  }

  /**
   * The server, room and token to write with: null while not connected, or when this visitor may not change the
   * open slot (permissions.ts canEditArt, the hook for the admin system).
   */
  private writer(): { base: string; room: string; token: string } | null {
    const w = this.ctx.writer();
    return w && this.slot && canEditArt(w.room, this.slot) ? w : null;
  }

  private canWrite(): boolean {
    return !!this.writer() && !this.busy;
  }

  private hasFiles(e: DragEvent): boolean {
    return !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');
  }

  private render(): void {
    const { slots, artworks } = this.ctx;
    const s = this.slot ? slots.get(this.slot) : undefined;
    if (!s) return;
    const entry = artworks.entry(s.id);
    if (entry && artworks.loadFailed(s.id)) this.setStatus('The image did not load on the wall. Click its thumbnail to try again.', true);
    else if (this.status.textContent === 'Loading the image…' && artworks.shown(s.id) === entry?.hash) this.setStatus('');
    this.title.textContent = s.kind === OVERLAY_KIND ? 'Overlay image' : entry ? 'Picture' : s.over.length ? 'Large picture' : 'Picture';
    this.where.textContent = s.name;
    // the picture area takes the picture aspect of the frame it hangs in (the uploading image's, the hung one's, or
    // the slot's default frame), with or without a mat
    const fin = this.shownChoice();
    const fit = entry && !this.localUrl ? frameFor(s, entry)
      : frameFor(s, this.localSize ? { ...this.localSize, mat: fin.mat } : { mat: fin.mat });
    this.drop.style.setProperty('--ar', (fit.layout.imageW / fit.layout.imageH).toFixed(4));
    this.drop.classList.toggle('art-drop--large', s.over.length > 0);

    // frame choice (surface slots, the window view and the gold oval, have no frame colour and no mat)
    // read only: connected, but this browser may not change pictures here (the edit lock, permissions.ts)
    const cw = this.ctx.writer();
    const readOnly = !!cw && !canEditArt(cw.room, s.id);
    this.finishEl.hidden = this.matEl.hidden = frameless(s.kind) || readOnly;
    this.actions.hidden = this.lib.hidden = this.note.hidden = readOnly;
    const large = slots.largeOf(s.id);
    const showSize = !!large && !entry && !readOnly;
    this.sizeGroup.hidden = !showSize;
    if (showSize && large) {
      const isLarge = s.id === large.id;
      this.sizeLarge.checked = isLarge;
      this.sizePanel.checked = !isLarge;
      const blockers = large.over.filter((o) => artworks.has(o));
      this.sizeLarge.disabled = !isLarge && blockers.length > 0;
      this.sizePanel.disabled = isLarge && !slots.panelUnder(large.id);
      this.sizeNote.textContent = this.sizeLarge.disabled
        ? `The large frame covers ${blockers.map((b) => slots.get(b)?.name.replace(/^.*, /, '') ?? b).join(', ')}, which ${blockers.length > 1 ? 'have pictures' : 'has a picture'}. Remove ${blockers.length > 1 ? 'them' : 'it'} first.`
        : isLarge ? `Covers ${large.over.length} moulding panels; they stay empty while it is up.` : '';
      this.sizeNote.hidden = !this.sizeNote.textContent;
      if (this.sizeLarge.disabled) this.sizeLarge.setAttribute('aria-describedby', 'art-size-note');
      else this.sizeLarge.removeAttribute('aria-describedby');
    }

    // frame colour and mat: the hung picture's, or the ones chosen for the upload
    const writable = !!this.writer() && !this.busy;
    const locked = !!entry && !writable;
    const showRow = (r: ColourRow, values: string[], value: string, custom: string | null, label: string, presetHex: string | null) => {
      const anyChecked = values.includes(value) && custom === null;
      r.dots.forEach((d, i) => {
        const on = custom === null && values[i] === value;
        d.setAttribute('aria-checked', String(on));
        d.tabIndex = on || (!anyChecked && i === 0) ? 0 : -1;
        d.disabled = locked;
      });
      r.color.disabled = r.hex.disabled = locked;
      r.color.classList.toggle('is-on', custom !== null);
      r.hex.classList.toggle('is-on', custom !== null);
      // the colour field and hex value show the chosen colour: the custom one, or the preset's (not for "No mat")
      const shown = custom ?? presetHex;
      if (shown && document.activeElement !== r.color) r.color.value = shown;
      r.hexView.show(r.color.value.toLowerCase());
      // a custom colour is in the hex field
      r.name.textContent = custom !== null ? '' : label;
    };
    showRow(this.finishRow, FINISHES.map((f) => f.value), fin.finish, fin.finish === 'custom' ? fin.color ?? this.finishRow.color.value : null,
      FINISHES.find((f) => f.value === fin.finish)?.label ?? '', FINISHES.find((f) => f.value === fin.finish)?.hex ?? null);
    const matValue = fin.mat ?? ART_MAT_DEFAULT;
    const matPreset = MATS.find((m) => m.value === matValue);
    showRow(this.matRow, MATS.map((m) => m.value), matValue, matPreset ? null : matValue, matPreset?.label ?? '', matValue === ART_MAT_NONE ? null : matValue);

    // picture: the uploading file, the hung picture, or the drop zone
    const src = this.localUrl ?? (entry ? this.ctx.fileUrl(entry) : null);
    // a tile (small square image): repeated as on the wall, pixels sharp (CSS background, same layout as tile.ts)
    const tile = !this.localUrl && entry && isTile(entry.w, entry.h) ? tileLayout(entry.w, fit.layout.imageW, fit.layout.imageH) : null;
    this.drop.classList.toggle('art-drop--tile', !!tile);
    if (tile && src) {
      this.drop.style.backgroundImage = `url("${src}")`;
      this.drop.style.backgroundSize = `${(100 / tile.rx).toFixed(4)}% auto`;
      this.drop.setAttribute('role', 'img');
      this.drop.setAttribute('aria-label', entry?.title ? `Picture: ${entry.title}` : 'The picture in this frame');
    } else {
      this.drop.style.backgroundImage = this.drop.style.backgroundSize = '';
      this.drop.removeAttribute('role');
      this.drop.removeAttribute('aria-label');
    }
    if (src && !tile) {
      if (this.preview.getAttribute('src') !== src) this.preview.src = src;
      this.preview.alt = entry?.title ? `Picture: ${entry.title}` : 'The picture in this frame';
      this.preview.hidden = false;
    } else {
      this.preview.removeAttribute('src');
      this.preview.hidden = true;
    }
    this.drop.classList.toggle('art-drop--empty', !src);
    this.dropText.hidden = !!src || readOnly;
    // one span per item: the line wraps between items, not inside one ("1350 × 2400 px")
    this.meta.replaceChildren();
    if (entry) {
      const items = [entry.title, `added by ${entry.by}`, `${entry.w} × ${entry.h} px`].filter((t): t is string => !!t);
      items.forEach((t, i) => {
        if (i) this.meta.append(' · ');
        this.meta.append(h('span', { class: 'art-meta__item' }, t));
      });
    }
    this.meta.hidden = !entry;

    const writer = this.writer();
    this.addBtn.textContent = entry ? 'Replace image' : 'Add image';
    this.addBtn.disabled = !writer || this.busy;
    this.removeBtn.hidden = !entry;
    this.removeBtn.disabled = !writer || this.busy;
    this.note.textContent = s.kind === OVERLAY_KIND ? OVERLAY_NOTE : PICTURE_NOTE;
    this.offline.hidden = !!writer || readOnly;
    this.offline.textContent = OFFLINE_TEXT;
    this.renderLibrary(s.wall, entry);
  }

  private wallName(wall: string): string {
    for (const s of this.ctx.slots.slots.values()) if (s.wall === wall) return s.name.replace(/,.*$/, '');
    return wall;
  }

  /** The library grid of `wall`; `entry` = the picture in this slot (marked). Cells are kept per image (focus stays). */
  private renderLibrary(wall: string, entry: ArtEntry | undefined): void {
    const items = this.library.get(wall) ?? [];
    const writer = this.writer();
    const disabled = !writer || this.busy;
    this.libTitle.textContent = `${this.wallName(wall)} library`;
    this.libEmpty.hidden = items.length > 0;
    const hungHere = new Map<string, string[]>(); // hash -> slot names on this wall
    for (const s of this.ctx.slots.slots.values()) {
      if (s.wall !== wall) continue;
      const e = this.ctx.artworks.entry(s.id);
      if (e) hungHere.set(e.hash, [...(hungHere.get(e.hash) ?? []), s.name]);
    }
    const old = new Map<string, HTMLLIElement>();
    for (const li of Array.from(this.libGrid.children) as HTMLLIElement[]) old.set(li.dataset.hash!, li);
    const cells: HTMLLIElement[] = [];
    for (const it of items) {
      let li = old.get(it.hash);
      old.delete(it.hash);
      if (!li) li = this.libCell(wall, it.hash);
      const pick = li.querySelector<HTMLButtonElement>('.art-lib__thumb')!;
      const img = pick.querySelector('img')!;
      const del = li.querySelector<HTMLButtonElement>('.art-lib__del')!;
      const name = it.title ?? 'Untitled image';
      const current = !!entry && entry.hash === it.hash;
      const src = it.thumb ? this.ctx.fileUrl({ url: it.thumb } as ArtEntry) /* fileUrl only reads url */ : hungHere.has(it.hash) || current ? this.ctx.fileUrl(it as unknown as ArtEntry) : null;
      if (src && img.getAttribute('src') !== src) img.src = src;
      if (!src) img.removeAttribute('src');
      img.hidden = !src;
      li.classList.toggle('is-current', current);
      li.classList.toggle('is-tile', isTile(it.w, it.h));
      pick.setAttribute('aria-label', current ? `${name}: in this frame` : `Hang ${name} here`);
      pick.title = `${name} · ${it.w} × ${it.h} px · added by ${it.by}`;
      if (current) pick.setAttribute('aria-current', 'true');
      else pick.removeAttribute('aria-current');
      pick.disabled = disabled;
      const confirm = this.confirming?.wall === wall && this.confirming.hash === it.hash;
      const where = hungHere.get(it.hash);
      del.classList.toggle('is-confirm', confirm);
      li.classList.toggle('is-confirming', confirm);
      del.textContent = '';
      if (confirm) {
        del.append('Delete?');
        del.setAttribute('aria-label', `Delete ${name}${where ? ' and take it off the wall' : ''}: press again to confirm`);
      } else {
        del.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 3l6 6m0-6l-6 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
        del.setAttribute('aria-label', `Delete ${name} from the library`);
      }
      del.disabled = disabled;
      cells.push(li);
      if (!it.thumb && writer && (hungHere.has(it.hash) || current)) this.backfillThumb(wall, it);
    }
    for (const li of old.values()) li.remove();
    // keep the order newest first without re-creating cells
    cells.forEach((li, i) => { if (this.libGrid.children[i] !== li) this.libGrid.insertBefore(li, this.libGrid.children[i] ?? null); });
    const c = this.confirming;
    const ci = c && c.wall === wall ? items.find((i) => i.hash === c.hash) : undefined;
    const where = ci ? hungHere.get(ci.hash) : undefined;
    this.libNote.textContent = ci
      ? where
        ? `${ci.title ?? 'This image'} is on the wall (${where.map((n) => n.replace(/^.*, /, '')).join(', ')}). Deleting it also takes it down.`
        : `Delete ${ci.title ?? 'this image'} from the library? Press Delete? again.`
      : '';
    this.libNote.hidden = !this.libNote.textContent;
  }

  private libCell(wall: string, hash: string): HTMLLIElement {
    const li = h('li', { class: 'art-lib__cell', 'data-hash': hash });
    const pick = h('button', { type: 'button', class: 'art-lib__thumb' });
    const img = h('img', { alt: '', loading: 'lazy', decoding: 'async', draggable: 'false', crossorigin: 'anonymous' });
    pick.append(img);
    pick.addEventListener('click', () => void this.hangLibrary(hash));
    const del = h('button', { type: 'button', class: 'art-lib__del', 'aria-describedby': 'art-lib-note' });
    del.addEventListener('click', () => {
      if (this.confirming?.wall === wall && this.confirming.hash === hash) void this.deleteLibrary(wall, hash);
      else this.startConfirm(wall, hash);
    });
    li.append(pick, del);
    return li;
  }

  private startConfirm(wall: string, hash: string): void {
    this.cancelConfirm(false);
    this.confirming = { wall, hash, timer: setTimeout(() => this.cancelConfirm(), CONFIRM_MS) };
    this.refresh();
  }

  private cancelConfirm(render = true): void {
    if (!this.confirming) return;
    clearTimeout(this.confirming.timer);
    this.confirming = null;
    if (render) this.refresh();
  }

  /** Hang a library image in this slot: the server assigns the stored file (no upload). */
  private async hangLibrary(hash: string): Promise<void> {
    const slot = this.slot;
    const writer = this.writer();
    if (!slot || this.busy) return;
    if (!writer) { this.setStatus('Not connected to the room server: pictures cannot be changed now.', true); return; }
    const cur = this.ctx.artworks.entry(slot);
    if (cur?.hash === hash) {
      // already this slot's picture: if the wall does not show it (its image failed to load), load it again
      if (this.ctx.artworks.shown(slot) !== hash) { this.setStatus('Loading the image…'); this.ctx.artworks.retry(slot); }
      return;
    }
    const blockers = this.ctx.slots.conflicts(slot).filter((c) => this.ctx.artworks.has(c));
    if (blockers.length) { this.setStatus('Another picture is in the way: the large frame and the panels under it cannot both have a picture.', true); return; }
    this.cancelConfirm(false);
    this.flushFrame();
    this.busy = true;
    this.setStatus('Hanging…');
    this.render();
    try {
      // a filled slot keeps the frame it has; an empty one takes the frame chosen here
      const art = await hangFromLibrary(writer.base, writer.room, slot, writer.token, hash, this.nextFrame(cur));
      this.onApplied(slot, art);
      this.setStatus('');
    } catch (e) {
      this.setStatus(e instanceof ArtError ? e.message : `Could not hang the image: ${(e as Error).message}`, true);
    } finally {
      this.busy = false;
      if (this.slot === slot) this.render();
      if (this.slot === slot) this.libGrid.querySelector<HTMLButtonElement>(`[data-hash="${hash}"] .art-lib__thumb`)?.focus({ preventScroll: true });
    }
  }

  /** Delete a library image (second press of its delete button); slots on the wall showing it are emptied. */
  private async deleteLibrary(wall: string, hash: string): Promise<void> {
    const writer = this.writer();
    if (!writer || this.busy) return;
    const items = this.library.get(wall) ?? [];
    const k = items.findIndex((i) => i.hash === hash);
    const name = items[k]?.title ?? 'The image';
    this.cancelConfirm(false);
    this.busy = true;
    this.setStatus('Deleting…');
    this.render();
    try {
      const r = await deleteFromLibrary(writer.base, writer.room, wall, hash, writer.token);
      for (const slot of r.unhung) this.onApplied(slot, null);
      this.setLibrary(wall, r.library);
      this.setStatus(r.unhung.length ? `${name} was deleted and taken off the wall.` : `${name} was deleted.`);
    } catch (e) {
      this.setStatus(e instanceof ArtError ? e.message : `Could not delete the image: ${(e as Error).message}`, true);
    } finally {
      this.busy = false;
      this.render();
      // focus: the image that took its place in the grid, else the one before, else the panel
      const cells = this.libGrid.querySelectorAll<HTMLButtonElement>('.art-lib__thumb');
      const next = cells[Math.min(k, cells.length - 1)];
      if (next) next.focus({ preventScroll: true });
      else this.el.focus({ preventScroll: true });
    }
  }

  /**
   * An image without a thumbnail that hangs on the wall (its file is already loaded for the frame): make the
   * thumbnail from it and send it, once per image.
   */
  private backfillThumb(wall: string, it: LibraryItem): void {
    if (this.thumbTried.has(it.hash)) return;
    this.thumbTried.add(it.hash);
    const writer = this.writer();
    if (!writer) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = this.ctx.fileUrl(it as unknown as ArtEntry);
    img.decode().then(() => makeThumb(img, img.naturalWidth, img.naturalHeight)).then((t) => {
      const w = this.writer();
      if (!t || !w) return;
      return uploadThumb(w.base, w.room, wall, it.hash, w.token, t).then((item) => {
        if (!item) return;
        const list = (this.library.get(wall) ?? []).map((i) => (i.hash === item.hash ? item : i));
        this.setLibrary(wall, list);
      });
    }).catch((e) => console.info('[art] thumbnail not sent', e?.message ?? e));
  }

  private chooseSize(v: 'panel' | 'large'): void {
    const s = this.slot ? this.ctx.slots.get(this.slot) : undefined;
    if (!s) return;
    const large = this.ctx.slots.largeOf(s.id);
    if (!large) return;
    const target = v === 'large' ? large : s.id === large.id ? this.ctx.slots.panelUnder(large.id) : s;
    if (target && target.id !== s.id) this.onSelectSlot(target.id);
  }

  /** A broadcast picture for `slot`: while this panel has an unconfirmed frame change there, keep showing that. */
  incoming(slot: string, art: ArtEntry | null): ArtEntry | null {
    const p = this.pending;
    if (!art || !p || p.slot !== slot || art.hash !== p.before.hash) return art;
    return withChoice(art, p.choice);
  }

  /**
   * The frame for a picture going into a slot (upload or library): a filled slot keeps the frame it has (fields it
   * does not set stay the slot defaults); an empty one takes the frame chosen here.
   */
  private nextFrame(cur: ArtEntry | undefined): { finish?: ArtFinish; color?: string; mat?: string } {
    if (cur) return { finish: cur.finish, color: cur.color, mat: cur.mat };
    return this.uploadChoice ?? {};
  }

  /** The frame the controls show: the hung picture's, else the one chosen for the upload, else the slot default. */
  private shownChoice(): FrameChoice {
    const s = this.slot ? this.ctx.slots.get(this.slot) : undefined;
    if (!s) return { finish: 'oak' };
    const entry = this.ctx.artworks.entry(s.id);
    if (entry) return { finish: this.ctx.artworks.finishOf(s, entry), color: entry.color, mat: entry.mat };
    return this.uploadChoice ?? { finish: s.spec.finish };
  }

  /**
   * A change to the frame (finish and colour, or mat; the rest stays as shown). Empty slot: kept for the upload.
   * Filled slot: shown at once; 'send' sends it after FINISH_SEND_MS (later changes restart the wait), 'preview'
   * only shows it (the colour field while it moves).
   */
  private choose(change: Partial<FrameChoice>, mode: 'preview' | 'send'): void {
    const slot = this.slot;
    const entry = slot ? this.ctx.artworks.entry(slot) : undefined;
    const choice: FrameChoice = { ...this.shownChoice(), ...change };
    if (choice.finish !== 'custom') delete choice.color;
    if (!slot || !entry) {
      this.uploadChoice = choice;
      this.render();
      return;
    }
    if (!this.writer() || this.busy) return;
    let p = this.pending;
    if (p && p.slot !== slot) { this.flushFrame(); p = null; }
    if (!p) {
      if (sameChoice(choice, { finish: this.ctx.artworks.finishOf(this.ctx.slots.get(slot)!, entry), color: entry.color, mat: entry.mat })) return;
      p = this.pending = { slot, choice, before: entry, timer: null, inflight: 0 };
    }
    p.choice = choice;
    if (p.timer) clearTimeout(p.timer);
    p.timer = mode === 'send' ? setTimeout(() => this.flushFrame(), FINISH_SEND_MS) : null;
    this.onApplied(slot, withChoice(entry, choice));
    this.render();
  }

  /** Send the waiting frame change now; on failure the frame goes back to what it was. */
  private flushFrame(): void {
    const p = this.pending;
    if (!p) return;
    if (p.timer) clearTimeout(p.timer);
    p.timer = null;
    const choice = p.choice;
    const writer = this.writer();
    const done = () => { if (this.pending === p && !p.inflight && !p.timer) this.pending = null; };
    const undo = (msg: string) => {
      if (this.pending === p) this.pending = null;
      const cur = this.ctx.artworks.entry(p.slot);
      if (cur && cur.hash === p.before.hash) this.onApplied(p.slot, p.before);
      if (this.slot === p.slot) this.setStatus(msg, true);
      this.refresh();
    };
    if (!writer) { undo('Not connected to the room server: pictures cannot be changed now.'); return; }
    p.inflight++;
    setArtFrame(writer.base, writer.room, p.slot, writer.token, choice).then((art) => {
      p.inflight--;
      // the answer of the newest request (nothing else waiting) is the frame as stored
      if (!p.inflight && !p.timer && sameChoice(p.choice, choice)) {
        const cur = this.ctx.artworks.entry(p.slot);
        if (cur && cur.hash === art.hash) this.onApplied(p.slot, art);
      }
      done();
    }, (e) => { p.inflight--; undo(e instanceof ArtError ? e.message : `Could not change the frame: ${(e as Error).message}`); });
  }

  private setStatus(text: string, error = false): void {
    this.status.textContent = text;
    this.status.classList.toggle('art-status--error', error && !!text);
  }

  private async upload(file: File): Promise<void> {
    const slot = this.slot;
    const s = slot ? this.ctx.slots.get(slot) : undefined;
    const writer = this.writer();
    if (!s || !slot || this.busy) return;
    if (!writer) { this.setStatus('Not connected to the room server: pictures cannot be changed now.', true); return; }
    const blockers = this.ctx.slots.conflicts(slot).filter((c) => this.ctx.artworks.has(c));
    if (blockers.length) { this.setStatus('Another picture is in the way: the large frame and the panels under it cannot both have a picture.', true); return; }
    this.busy = true;
    this.setStatus('Preparing the image…');
    this.render();
    try {
      // the original aspect (no crop): the image goes into the wall's library and may hang in either frame kind
      const img = await prepareImage(file, this.ctx.slots.maxPx, 0, (text) => this.setStatus(text));
      this.localUrl = URL.createObjectURL(img.blob);
      this.localSize = { w: img.w, h: img.h };
      this.setStatus(`Uploading (${(img.blob.size / 1024).toFixed(0)} KB, ${img.w} × ${img.h}${img.frames ? `, ${img.frames} frames` : ''})…`);
      this.render();
      // a replaced picture keeps the frame it hangs in
      const up = await uploadArt(writer.base, writer.room, slot, writer.token, img, this.nextFrame(this.ctx.artworks.entry(slot)));
      this.onApplied(slot, up.art);
      if (up.wall && up.library) this.setLibrary(up.wall, up.library);
      this.setStatus('');
      // the library thumbnail (a failure only leaves the grid showing a blank tile)
      const item = up.wall ? this.library.get(up.wall)?.find((i) => i.hash === up.art.hash) : undefined;
      if (img.thumb && up.wall && item && !item.thumb) {
        this.thumbTried.add(up.art.hash);
        const wall = up.wall;
        uploadThumb(writer.base, writer.room, wall, up.art.hash, writer.token, img.thumb).then((t) => {
          if (t) this.setLibrary(wall, (this.library.get(wall) ?? []).map((i) => (i.hash === t.hash ? t : i)));
        }, (e) => console.info('[art] thumbnail not sent', e?.message ?? e));
      }
    } catch (e) {
      this.setStatus(e instanceof ArtError ? e.message : `Upload failed: ${(e as Error).message}`, true);
    } finally {
      this.busy = false;
      if (this.localUrl) {
        const u = this.localUrl;
        this.localUrl = null;
        this.localSize = null;
        // keep the local preview until the server copy has loaded in the <img>
        setTimeout(() => URL.revokeObjectURL(u), 5000);
      }
      if (this.slot === slot) this.render();
    }
  }

  private async remove(): Promise<void> {
    const slot = this.slot;
    const writer = this.writer();
    if (!slot || !writer || this.busy) return;
    this.busy = true;
    this.setStatus('Removing…');
    this.render();
    try {
      await removeArt(writer.base, writer.room, slot, writer.token);
      this.onApplied(slot, null);
      this.setStatus('Removed.');
      this.addBtn.focus({ preventScroll: true });
    } catch (e) {
      this.setStatus(e instanceof ArtError ? e.message : `Remove failed: ${(e as Error).message}`, true);
    } finally {
      this.busy = false;
      if (this.slot === slot) this.render();
    }
  }
}

/** The entry with another frame (the colour only for 'custom'; the mat when the choice has one). */
function withChoice(e: ArtEntry, c: FrameChoice): ArtEntry {
  const out: ArtEntry = { ...e, finish: c.finish };
  if (c.finish === 'custom' && c.color) out.color = c.color;
  else delete out.color;
  if (c.mat) out.mat = c.mat;
  return out;
}
